// src/store/workflow/workflowThunks.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
	getTaskVariables,
	deletePendingTask,
	getFormByUserTask,
	setAssigneeForTask,
	putProcessInstanceVariable,
	postRegeneratePDF,
	unclaimPendingTask,
	claimTask,
	cloneWorkflow,
	startWorkflowProcess,
	getWorkflowsList,
} from "api/workflowsApi";
import { logger } from "helpers/logger";

// Thunk for getting task variables
export const fetchTaskVariables = createAsyncThunk("workflow/fetchTaskVariables", async ({ processInstanceId }, { rejectWithValue, signal }) => {
	try {
		const response = await getTaskVariables(processInstanceId, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch task variables:", error);
		return rejectWithValue(error.message || "Failed to fetch task variables");
	}
});

export const deletePendingTaskThunk = createAsyncThunk("workflow/deletePendingTask", async ({ rootProcessInstanceId }, { rejectWithValue, signal }) => {
	try {
		const response = await deletePendingTask({ task_id: rootProcessInstanceId }, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to delete pending task:", error);
		return rejectWithValue(error.message || "Failed to delete task");
	}
});

export const fetchFormByUserTaskThunk = createAsyncThunk("workflow/fetchFormByUserTask", async (params, { rejectWithValue, signal }) => {
	try {
		const { processInstanceId } = params;
		const response = await getFormByUserTask(processInstanceId, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch form by user task:", error);
		return rejectWithValue(error.message || "Failed to fetch form by user task");
	}
});

export const putAssigneeForTaskThunk = createAsyncThunk("workflow/putAssigneeForTaskThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const assigneeData = params;
		const response = await setAssigneeForTask(assigneeData, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to set assignee:", error);
		return rejectWithValue(error.message || "Failed to set assignee");
	}
});

export const putProcessInstanceVariableThunk = createAsyncThunk("workflow/putProcessInstanceVariableThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { processInstanceId, variableName, jsonData } = params;
		const response = await putProcessInstanceVariable(processInstanceId, variableName, jsonData, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to put process instance variable:", error);
		return rejectWithValue(error.message || "Failed to put process instance variable");
	}
});

export const postRegeneratePDFThunk = createAsyncThunk("workflow/postRegeneratePDFThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { applicationID, templateId, tableId } = params;
		const response = await postRegeneratePDF(applicationID, templateId, tableId, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to regenerate PDF:", error);
		return rejectWithValue(error.message || "Failed to regenerate PDF");
	}
});

export const unclaimPendingTaskThunk = createAsyncThunk("workflow/unclaimPendingTaskThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { taskId, userId } = params;
		const response = await unclaimPendingTask(taskId, userId, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to unclaim pending task:", error);
		return rejectWithValue(error.message || "Failed to Unclaim Task");
	}
});

export const claimTaskThunk = createAsyncThunk("workflow/claimTaskThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { taskId, userId } = params;
		const response = await claimTask(taskId, userId, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to claim task:", error);
		return rejectWithValue(error.message || "Failed to Claim Task");
	}
});

export const cloneWorkflowThunk = createAsyncThunk("workflow/cloneWorkflowThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { applicationId, assignee, processInstanceId } = params;
		const response = await cloneWorkflow(applicationId, assignee, processInstanceId, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to clone workflow:", error);
		return rejectWithValue(error.message || "Failed to Clone Workflow");
	}
});

export const startWorkflowProcessThunk = createAsyncThunk("workflow/startWorkflowProcessThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { definitionKey, assignee } = params;
		const response = await startWorkflowProcess(definitionKey, assignee, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to start workflow process:", error);
		return rejectWithValue(error.message || "Failed to Start Workflow Process");
	}
});

export const getWorkflowsListThunk = createAsyncThunk("workflow/getWorkflowsListThunk", async (params, { rejectWithValue }) => {
	try {
		const { userId, companyId } = params;
		const response = await getWorkflowsList(userId, companyId);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to get workflows list:", error);
		return rejectWithValue(error.message || "Failed to Get Workflow List.");
	}
});
